import { useMemo, useRef, useState } from "react";
import { useTranslation } from "@/common/providers/i18n-provider";
import {
  MAX_CSV_PROFILES,
  MAX_CSV_PROFILE_LABEL_LENGTH,
  checkProfileCompatibility,
  createProfileId,
  type CsvMappingProfile,
} from "@/lib/csv-mapping-profiles";
import {
  CSV_PROFILE_FILE_MEDIA_TYPE,
  MAX_CSV_PROFILE_FILE_BYTES,
  parseCsvProfileFile,
  previewCsvProfileImport,
  savedCountAfter,
  type CsvProfileFileIssue,
  type CsvProfileFileIssueReason,
  type CsvProfileImportAction,
  type CsvProfileImportConflictKind,
  type CsvProfileImportPreviewItem,
} from "@/lib/csv-profile-file";
import { readLocalFile } from "@/lib/local-file";
import { trackCsvProfileAction } from "@/lib/tools-analytics";
import {
  SAVE_PROFILES_ERROR_KEYS,
  type SaveProfilesResult,
} from "./csv-profile-storage";

/** Reasons share copy where the reader's remedy is the same. */
const ISSUE_KEYS: Record<CsvProfileFileIssueReason, string> = {
  "too-large": "toolsCsvProfileFile.issue.tooLarge",
  "profile-too-large": "toolsCsvProfileFile.issue.tooLarge",
  "not-utf8": "toolsCsvProfileFile.issue.notProfileFile",
  "not-json": "toolsCsvProfileFile.issue.notProfileFile",
  "not-envelope": "toolsCsvProfileFile.issue.notProfileFile",
  "unsupported-format": "toolsCsvProfileFile.issue.notProfileFile",
  "unsupported-version": "toolsCsvProfileFile.issue.unsupportedVersion",
  "unsupported-profile-version": "toolsCsvProfileFile.issue.unsupportedVersion",
  "no-profiles": "toolsCsvProfileFile.issue.profileCount",
  "too-many-profiles": "toolsCsvProfileFile.issue.profileCount",
  "forbidden-field": "toolsCsvProfileFile.issue.forbiddenField",
  "unknown-field": "toolsCsvProfileFile.issue.unknownField",
  "invalid-profile": "toolsCsvProfileFile.issue.invalidProfile",
  "duplicate-id": "toolsCsvProfileFile.issue.invalidProfile",
};

const ACTION_KEYS: Record<CsvProfileImportAction, string> = {
  add: "toolsCsvProfileFile.action.add",
  skip: "toolsCsvProfileFile.action.skip",
  replace: "toolsCsvProfileFile.action.replace",
  "keep-both": "toolsCsvProfileFile.action.keepBoth",
};

const CONFLICT_KEYS: Record<CsvProfileImportConflictKind, string> = {
  "same-id": "toolsCsvProfileFile.conflictId",
  "same-label": "toolsCsvProfileFile.conflictLabel",
};

type Notice = { tone: "ok" | "error"; text: string; issues?: string[] };

type BuildResult =
  | { ok: true; next: CsvMappingProfile[]; added: number; replaced: number }
  | { ok: false; reason: "replace-twice" };

/**
 * Fold the reader's per-profile choices into one new saved collection. Pure:
 * the caller writes the result in a single store call or not at all, so a
 * choice set can never half-apply. `replace` keeps the saved profile's id and
 * position (the reader's selection stays valid); `keep-both` gets a fresh id
 * unused by any saved or incoming profile and a "(imported)"-style label so
 * the two copies stay distinguishable in the picker.
 */
function buildImportedCollection(
  saved: readonly CsvMappingProfile[],
  items: readonly CsvProfileImportPreviewItem[],
  actionFor: (item: CsvProfileImportPreviewItem) => CsvProfileImportAction,
  copyLabel: (profile: CsvMappingProfile) => string,
): BuildResult {
  const next = [...saved];
  const usedIds = new Set([
    ...saved.map((profile) => profile.id),
    ...items.map((item) => item.profile.id),
  ]);
  const replacedIds = new Set<string>();
  let added = 0;
  for (const item of items) {
    const action = actionFor(item);
    if (action === "skip") continue;
    if (action === "add") {
      next.push(item.profile);
      added += 1;
      continue;
    }
    if (item.conflict === null) continue;
    if (action === "replace") {
      const targetId = item.conflict.existingId;
      if (replacedIds.has(targetId)) return { ok: false, reason: "replace-twice" };
      replacedIds.add(targetId);
      const position = next.findIndex((profile) => profile.id === targetId);
      if (position === -1) continue;
      next[position] = { ...item.profile, id: targetId };
      continue;
    }
    let id = createProfileId();
    while (usedIds.has(id)) id = createProfileId();
    usedIds.add(id);
    next.push({
      ...item.profile,
      id,
      label: copyLabel(item.profile).slice(0, MAX_CSV_PROFILE_LABEL_LENGTH),
    });
    added += 1;
  }
  return { ok: true, next, added, replaced: replacedIds.size };
}

/**
 * Import mapping profiles from a reader-chosen local JSON file, in three
 * explicit steps that never blur into each other:
 *
 * 1. **Choose** — the file is size-checked before it is read, then validated
 *    all-or-nothing by `parseCsvProfileFile`. A refused file shows its typed
 *    issues and stages nothing; choosing or cancelling never writes storage
 *    and never calls `onApply`.
 * 2. **Save choices** — each staged profile gets an explicit action (save as
 *    new, don't save, replace, keep both). Replace is never the default. The
 *    whole choice set lands through one `onCommit` call or not at all; if the
 *    store is blocked or full the prior saved state stays and the staged
 *    profiles remain for session use.
 * 3. **Apply** — a separate per-profile button hands the validated profile to
 *    the parent's compatibility review, exactly like loading a saved one. It
 *    touches only configuration; the pasted CSV stays as it is.
 *
 * Everything the file supplies renders as React text (escaped), never HTML.
 * File outcomes emit bounded `csv_profile` actions (`import_valid`,
 * `import_refused`, `import_saved`; `import_apply` fires in the parent's
 * review) — enums and the preset slug only, never file contents.
 */
export function CsvProfileImportControls({
  saved,
  headers,
  presetSlug,
  displayName,
  onCommit,
  onApply,
}: {
  /** The currently saved profiles (read-only here). */
  saved: readonly CsvMappingProfile[];
  /** The parsed file's headers, or `null` before any headered input. */
  headers: readonly string[] | null;
  /** The current route's preset slug, or `null` on the generic converter. */
  presetSlug: string | null;
  displayName: (profile: CsvMappingProfile) => string;
  /** Persist a whole new collection atomically; the only storage write. */
  onCommit: (next: CsvMappingProfile[]) => SaveProfilesResult;
  /** Apply one validated profile through the compatibility review. */
  onApply: (profile: CsvMappingProfile) => void;
}) {
  const { t } = useTranslation();
  const [staged, setStaged] = useState<CsvMappingProfile[] | null>(null);
  const [choices, setChoices] = useState<Record<number, CsvProfileImportAction>>(
    {},
  );
  const [notice, setNotice] = useState<Notice | null>(null);
  // Bumped on every choose; a read that finishes after a newer choose is
  // stale and must not overwrite the newer file's review or refusal.
  const chooseGeneration = useRef(0);

  // Recomputed against the live saved list, so a save made while the review
  // is open can never leave a stale conflict verdict behind.
  const preview = useMemo(
    () => (staged === null ? null : previewCsvProfileImport(staged, saved)),
    [staged, saved],
  );

  const actionFor = (item: CsvProfileImportPreviewItem): CsvProfileImportAction => {
    const chosen = choices[item.index];
    return chosen !== undefined && item.allowedActions.includes(chosen)
      ? chosen
      : item.proposedAction;
  };

  const savedName = (id: string): string => {
    const existing = saved.find((profile) => profile.id === id);
    return existing === undefined ? "" : displayName(existing);
  };

  const issueText = (issue: CsvProfileFileIssue): string => {
    let text = t(ISSUE_KEYS[issue.reason], { max: String(MAX_CSV_PROFILES) });
    if (issue.key !== undefined) {
      text = t("toolsCsvProfileFile.issueField", { message: text, key: issue.key });
    }
    if (issue.profileIndex !== null) {
      text = t("toolsCsvProfileFile.issueProfile", {
        number: String(issue.profileIndex + 1),
        message: text,
      });
    }
    return text;
  };

  function refuse(issues: CsvProfileFileIssue[]): void {
    trackCsvProfileAction("import_refused", presetSlug);
    clearStaged();
    setNotice({
      tone: "error",
      text: t("toolsCsvProfileFile.refused"),
      issues: issues.map(issueText),
    });
  }

  async function handleFile(file: File | undefined): Promise<void> {
    // A dismissed chooser delivers no file: nothing to do, nothing changes.
    if (file === undefined) return;
    const generation = ++chooseGeneration.current;
    const read = await readLocalFile(file, MAX_CSV_PROFILE_FILE_BYTES);
    if (generation !== chooseGeneration.current) return;
    if (!read.ok) {
      const reason = read.reason === "too-large" ? "too-large" : "not-utf8";
      refuse([{ reason, profileIndex: null }]);
      return;
    }
    const result = parseCsvProfileFile(read.bytes);
    if (!result.ok) {
      refuse(result.issues);
      return;
    }
    trackCsvProfileAction("import_valid", presetSlug);
    setStaged(result.profiles);
    setChoices({});
    setNotice(null);
  }

  function clearStaged(): void {
    setStaged(null);
    setChoices({});
  }

  function handleCancel(): void {
    clearStaged();
    setNotice({ tone: "ok", text: t("toolsCsvProfileFile.unchanged") });
  }

  function handleCommit(): void {
    if (preview === null) return;
    const built = buildImportedCollection(saved, preview.items, actionFor, (profile) =>
      t("toolsCsvProfileFile.copyLabel", { name: displayName(profile) }),
    );
    if (!built.ok) {
      setNotice({ tone: "error", text: t("toolsCsvProfileFile.replaceTwice") });
      return;
    }
    if (built.added === 0 && built.replaced === 0) {
      clearStaged();
      setNotice({ tone: "ok", text: t("toolsCsvProfileFile.unchanged") });
      return;
    }
    const result = onCommit(built.next);
    if (!result.ok) {
      // Prior saved state is untouched (one write, refused whole). Keep the
      // staged profiles so the reader can still use them this session.
      setNotice({
        tone: "error",
        text: `${t(SAVE_PROFILES_ERROR_KEYS[result.reason])} ${t("toolsCsvProfileFile.unsaved")}`,
      });
      return;
    }
    trackCsvProfileAction("import_saved", presetSlug);
    clearStaged();
    setNotice({
      tone: "ok",
      text: t("toolsCsvProfileFile.imported", {
        added: String(built.added),
        replaced: String(built.replaced),
      }),
    });
  }

  function handleApply(profile: CsvMappingProfile): void {
    onApply(profile);
    setNotice({
      tone: "ok",
      text: t("toolsCsvProfileFile.sessionOnly", { name: displayName(profile) }),
    });
  }

  const overCapacity =
    preview !== null &&
    savedCountAfter(saved.length, preview.items, actionFor) > MAX_CSV_PROFILES;

  return (
    <div className="tool-settings-start">
      <h3 className="tool-label-text">{t("toolsCsvProfileFile.title")}</h3>
      <p className="tool-local-note">{t("toolsCsvProfileFile.description")}</p>
      <div className="tool-settings-grid">
        <label className="tool-control-label">
          <span className="tool-label-text">
            {t("toolsCsvProfileFile.chooseLabel")}
          </span>
          <input
            type="file"
            accept={`.json,${CSV_PROFILE_FILE_MEDIA_TYPE}`}
            className="tool-control"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Reset so choosing the same file again re-reads it.
              event.target.value = "";
              void handleFile(file);
            }}
          />
        </label>
      </div>
      {preview !== null && (
        <div role="group" aria-label={t("toolsCsvProfileFile.previewTitle")}>
          <p className="tool-label-text">
            {t("toolsCsvProfileFile.previewTitle")}
          </p>
          <ul className="tool-settings-start">
            {preview.items.map((item) => {
              const profile = item.profile;
              const compat = checkProfileCompatibility(profile, headers, presetSlug);
              const mappedCount = Object.values(profile.mapping).filter(
                (name) => name !== null,
              ).length;
              return (
                <li key={item.index} data-import-index={item.index}>
                  {/* Reader-entered name: its own bidi direction, never the page's. */}
                  <p className="tool-label-text" dir="auto">
                    {displayName(profile)}
                  </p>
                  <p className="tool-local-note">
                    {t("toolsCsvProfileFile.summary", {
                      mapped: String(mappedCount),
                      categories: String(
                        Object.keys(profile.config.categoryAccounts ?? {}).length,
                      ),
                      rules: String(profile.config.descriptionRules?.length ?? 0),
                    })}
                  </p>
                  {compat.presetMismatch && (
                    <p className="tool-local-note">
                      {t("toolsCsvProfileFile.presetMismatch")}
                    </p>
                  )}
                  {item.conflict !== null && (
                    <p className="tool-local-note">
                      {t(CONFLICT_KEYS[item.conflict.kind], {
                        name: savedName(item.conflict.existingId),
                      })}
                      {item.conflict.identical &&
                        ` ${t("toolsCsvProfileFile.identical")}`}
                    </p>
                  )}
                  <div className="tool-file-actions">
                    <label className="tool-control-label">
                      <span className="tool-label-text">
                        {t("toolsCsvProfileFile.actionLabel")}
                      </span>
                      <select
                        value={actionFor(item)}
                        onChange={(event) =>
                          setChoices((prev) => ({
                            ...prev,
                            [item.index]: event.target.value as CsvProfileImportAction,
                          }))
                        }
                        className="tool-control"
                      >
                        {item.allowedActions.map((action) => (
                          <option key={action} value={action}>
                            {t(ACTION_KEYS[action])}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button type="button" onClick={() => handleApply(profile)}>
                      {t("toolsCsvProfileFile.apply")}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          {overCapacity && (
            <p className="tool-notice" role="alert">
              {t("toolsCsvProfileFile.overCapacity", {
                max: String(MAX_CSV_PROFILES),
              })}
            </p>
          )}
          <div className="tool-file-actions tool-settings-start">
            <button type="button" onClick={handleCommit} disabled={overCapacity}>
              {t("toolsCsvProfileFile.saveChoices")}
            </button>
            <button type="button" onClick={handleCancel}>
              {t("toolsCsvProfileFile.cancel")}
            </button>
          </div>
        </div>
      )}
      {notice !== null && (
        <div
          role={notice.tone === "error" ? "alert" : "status"}
          className={notice.tone === "error" ? "tool-notice" : "tool-status"}
        >
          <p>{notice.text}</p>
          {notice.issues !== undefined && notice.issues.length > 0 && (
            <ul>
              {notice.issues.map((issue, index) => (
                <li key={index}>{issue}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
